import bundledDefaultSiteDataJson from '../../public/data/default.json?raw';

// Dual-mode API: localStorage in development, PHP in production (cPanel)
// Architecture: default.json (shipped with code) + site.json (created by admin saves, never in source)
// In production: only use the PHP API layer so JSON files never need to be publicly requested
// In development: deep-merge local overrides with bundled defaults

export interface Alpaca {
  id: number;
  name: string;
  age: number;
  birthDate?: string;
  color: string;
  description: string;
  image: string;
  funFact?: string;
}

export const calculateAge = (birthDate: string): number => {
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

export interface Activity {
  id: number;
  title: string;
  description: string;
  price: string;
  image: string;
  details: string;
  allergyInfo?: string;
  ribbonEnabled?: boolean;
  ribbonText?: string;
  ribbonHomepage?: boolean;
  ribbonColor?: 'yellow' | 'pink';
}

export interface FAQ {
  id: number;
  question: string;
  answer: string;
}

export interface HomepageSettings {
  hero: {
    badge: string;
    title: string;
    subtitle: string;
    image: string;
  };
  features: Array<{
    id: number;
    title: string;
    description: string;
    icon:
      | 'heart'
      | 'leaf'
      | 'users'
      | 'sparkles'
      | 'star'
      | 'sun'
      | 'cloud'
      | 'coffee'
      | 'camera'
      | 'music'
      | 'gift'
      | 'award'
      | 'smile'
      | 'thumbsUp'
      | 'home'
      | 'tree'
      | 'flower'
      | 'paw';
  }>;
  homepageNote: string;
  reservation: {
    title: string;
    description: string;
    buttonText: string;
    buttonUrl: string;
  };
  aboutPreview: {
    title: string;
    text: string;
    buttonText: string;
  };
}

export interface EditableContentSection {
  id: number;
  title: string;
  content: string;
}

export interface CollaborationPartner {
  id: number;
  name: string;
  description: string;
  image: string;
  url?: string;
}

export interface SiteSettings {
  contact: {
    email: string;
    phone: string;
    phoneDisplay: string;
    address: string;
  };
  social: {
    facebook: string;
    instagram: string;
    tiktok?: string;
  };
  footer: {
    description: string;
    copyright: string;
  };
  openingHours: string;
  openingHoursNoteFooter: string;
  openingHoursNoteContact: string;
  reservationInfo: string;
  aboutAlpacas?: string;
  alpacasIntro?: string;
  activitiesInfo?: string;
  alpacaFactsTitle?: string;
  alpacaFacts?: string;
  alpacaFactsBullet?: string;
  walkingAlpacaEnabled?: boolean;
  walkingAlpacaMobileDisabled?: boolean;
  contactFaqText?: string;
  phoneCardEnabled?: boolean;
  giftVoucherWidgetEnabled?: boolean;
  giftVoucherTooltipText?: string;
  homepageActivitiesMoreButtonEnabled?: boolean;
  homepageActivitiesRandomizedEnabled?: boolean;
  aboutPage?: {
    heroTitle: string;
    heroIntro: string;
    teamImage: string;
    teamImageAlt: string;
    content?: string;
    sections?: EditableContentSection[];
    contentImages?: Array<{
      id: number;
      image: string;
      alt: string;
      afterParagraph: number;
      side: 'left' | 'right';
    }>;
    ctaTitle?: string;
    ctaText?: string;
    ctaButtonText?: string;
  };
  collaborationsPage?: {
    heroTitle: string;
    heroIntro: string;
    content?: string;
    sections?: EditableContentSection[];
    partners: CollaborationPartner[];
    contactButtonText: string;
    ctaTitle?: string;
    ctaText?: string;
  };
  activitiesGiftVoucher?: {
    title: string;
    description: string;
    buttonText: string;
  };
}

export interface ReviewsData {
  reviews: Array<{
    name: string;
    rating: number;
    text: string;
    date: string;
  }>;
  totalReviews: number;
  averageRating: number;
  lastUpdated: string;
}

export interface UploadedImage {
  id: string;
  name: string;
  url: string;
  uploadedAt: string;
}

// The consolidated site data structure
export interface SiteData {
  settings: SiteSettings;
  homepage: HomepageSettings;
  alpacas: Alpaca[];
  activities: Activity[];
  faqs: FAQ[];
  reviews: ReviewsData;
}

const getEmergencyFallbackData = (): SiteData => ({
  settings: {
    contact: { email: '', phone: '', phoneDisplay: '', address: '' },
    social: { facebook: '', instagram: '', tiktok: '' },
    footer: { description: '', copyright: '' },
    openingHours: '',
    openingHoursNoteFooter: '',
    openingHoursNoteContact: '',
    reservationInfo: '',
  },
  homepage: {
    hero: { badge: '', title: '', subtitle: '', image: '' },
    features: [],
    homepageNote: '',
    reservation: { title: '', description: '', buttonText: '', buttonUrl: '' },
    aboutPreview: { title: '', text: '', buttonText: '' },
  },
  alpacas: [],
  activities: [],
  faqs: [],
  reviews: { reviews: [], totalReviews: 0, averageRating: 0, lastUpdated: '' },
});

const parseBundledDefaultData = (): SiteData => {
  try {
    return JSON.parse(bundledDefaultSiteDataJson) as SiteData;
  } catch {
    return getEmergencyFallbackData();
  }
};

const bundledDefaultData = parseBundledDefaultData();

const cloneSiteData = (data: SiteData): SiteData => JSON.parse(JSON.stringify(data)) as SiteData;

const getBundledDefaultData = (): SiteData => cloneSiteData(bundledDefaultData);

const STATIC_PAGE_ROUTES = [
  '/',
  '/activiteiten',
  '/alpacas',
  '/faq',
  '/contact',
  '/over-ons',
  '/samenwerkingen',
] as const;

const SITE_DATA_SCRIPT_ID = 'site-data-snapshot';
const SITE_DATA_READY_ATTRIBUTE = 'data-site-data-ready';
const APP_RENDERED_ATTRIBUTE = 'data-app-rendered';
const INLINE_SNAPSHOT_BYPASS_PARAM = '__freshData';

type WindowWithSiteData = Window & typeof globalThis & {
  __SITE_DATA__?: SiteData;
};

const shouldBypassInlineSnapshot = (): boolean => {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has(INLINE_SNAPSHOT_BYPASS_PARAM);
};

const markSiteDataReady = (source: 'inline' | 'api' | 'dev') => {
  if (typeof document === 'undefined') return;

  document.documentElement.setAttribute(SITE_DATA_READY_ATTRIBUTE, 'true');
  document.documentElement.setAttribute('data-site-data-source', source);
};

const getInjectedSiteData = (): SiteData | null => {
  if (typeof window === 'undefined' || shouldBypassInlineSnapshot()) return null;

  const injected = (window as WindowWithSiteData).__SITE_DATA__;
  return injected ? cloneSiteData(injected) : null;
};

const encodeSiteDataSnapshot = (siteData: SiteData): string => {
  const bytes = new TextEncoder().encode(JSON.stringify(siteData));
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary);
};

const buildInlineSiteDataScript = (siteData: SiteData): string => {
  const encoded = JSON.stringify(encodeSiteDataSnapshot(siteData));

  return `(()=>{const encoded=${encoded};const bytes=Uint8Array.from(atob(encoded),char=>char.charCodeAt(0));window.__SITE_DATA__=JSON.parse(new TextDecoder().decode(bytes));document.documentElement.setAttribute('${SITE_DATA_READY_ATTRIBUTE}','true');document.documentElement.setAttribute('data-site-data-source','inline');})();`;
};

const injectSiteDataSnapshot = (doc: Document, siteData: SiteData) => {
  doc.getElementById(SITE_DATA_SCRIPT_ID)?.remove();

  const script = doc.createElement('script');
  script.id = SITE_DATA_SCRIPT_ID;
  script.textContent = buildInlineSiteDataScript(siteData);

  const firstAppScript = Array.from(doc.querySelectorAll('script')).find((node) => {
    const src = node.getAttribute('src') || '';
    return node.getAttribute('type') === 'module' || src.includes('/assets/') || src.includes('/src/main.tsx');
  });

  if (firstAppScript?.parentNode) {
    firstAppScript.parentNode.insertBefore(script, firstAppScript);
    return;
  }

  doc.head.appendChild(script);
};

const sanitizeStaticDocument = (doc: Document, siteData: SiteData) => {
  injectSiteDataSnapshot(doc, siteData);
  doc.documentElement.removeAttribute(APP_RENDERED_ATTRIBUTE);
  doc.documentElement.setAttribute(SITE_DATA_READY_ATTRIBUTE, 'true');
  doc.documentElement.setAttribute('data-site-data-source', 'inline');
  doc.querySelector('link[rel="preload"][href*="api.php?action=site-data"]')?.remove();

  const widgetMount = doc.getElementById('nostium-widget');
  if (widgetMount) {
    widgetMount.innerHTML = '';
  }
};

// Environment detection
const isProduction = (): boolean => {
  const hostname = window.location.hostname;
  const isLovableHosting =
    hostname === 'localhost' ||
    hostname.includes('localhost') ||
    hostname.includes('lovable') ||
    hostname.includes('webcontainer') ||
    hostname.endsWith('.lovableproject.com') ||
    hostname.endsWith('.lovable.app');

  if (import.meta.env.DEV) {
    console.log('[API] Hostname:', hostname, '| Lovable hosting:', isLovableHosting, '| Production mode:', !isLovableHosting);
  }

  return !isLovableHosting;
};

// CSRF Token management
let csrfToken: string | null = null;

const getCsrfToken = async (): Promise<string> => {
  if (csrfToken) return csrfToken;
  if (isProduction()) {
    try {
      const response = await fetch('/api.php?action=csrf', {
        credentials: 'include',
        cache: 'no-store',
      });
      const data = await response.json();
      csrfToken = data.token;
      return csrfToken || '';
    } catch {
      return '';
    }
  }
  return '';
};

// ===== Deep merge utility =====

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const deepMerge = (defaults: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = { ...defaults };
  for (const key of Object.keys(source)) {
    const sourceValue = source[key];
    const defaultValue = defaults[key];
    if (isRecord(sourceValue) && isRecord(defaultValue)) {
      result[key] = deepMerge(defaultValue, sourceValue);
    } else {
      result[key] = sourceValue;
    }
  }
  return result;
};

// ===== Migration helpers =====

const normalizeCollaborationPartners = (partners: CollaborationPartner[] = []): CollaborationPartner[] => {
  return partners;
};

const migrateSiteSettings = (settings: SiteSettings): SiteSettings => {
  const result = { ...settings };

  if (result.aboutPage && !result.aboutPage.content && result.aboutPage.sections?.length) {
    result.aboutPage = {
      ...result.aboutPage,
      content: result.aboutPage.sections.map(s => s.content).filter(Boolean).join('\n\n'),
    };
  }

  if (result.collaborationsPage) {
    result.collaborationsPage = {
      ...result.collaborationsPage,
      partners: normalizeCollaborationPartners(result.collaborationsPage.partners),
      content:
        result.collaborationsPage.content ||
        result.collaborationsPage.sections?.map(s => s.content).filter(Boolean).join('\n\n') ||
        result.collaborationsPage.content,
    };
  }

  return result;
};

// ===== Consolidated data loading =====

let cachedDefaultData: SiteData | null = null;
let cachedBaseDefaultData: SiteData | null = null;
let cachedSiteData: Partial<SiteData> | null = null;
let dataLoadPromise: Promise<void> | null = null;

let productionBootstrapPromise: Promise<void> | null = null;
let staticPageRegenerationPromise: Promise<void> | null = null;

export class SiteBootstrapError extends Error {
  status: number;
  code: string;

  constructor(message: string, status = 500, code = 'bootstrap_failed') {
    super(message);
    this.name = 'SiteBootstrapError';
    this.status = status;
    this.code = code;
  }
}

const parseApiError = async (response: Response, fallbackMessage: string): Promise<SiteBootstrapError> => {
  try {
    const payload = await response.json() as { message?: string; code?: string };
    return new SiteBootstrapError(
      payload.message || fallbackMessage,
      response.status || 500,
      payload.code || 'bootstrap_failed'
    );
  } catch {
    return new SiteBootstrapError(fallbackMessage, response.status || 500);
  }
};

const fetchProductionSiteData = async (): Promise<SiteData> => {
  try {
    const response = await fetch('/api.php?action=site-data', {
      credentials: 'include',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw await parseApiError(response, 'Sitegegevens konden niet worden geladen.');
    }

    return await response.json() as SiteData;
  } catch (error) {
    if (error instanceof SiteBootstrapError) {
      throw error;
    }

    throw new SiteBootstrapError('De sitegegevens zijn tijdelijk niet beschikbaar.', 503, 'site_data_unreachable');
  }
};

const fetchProductionReviews = async (): Promise<ReviewsData | null> => {
  try {
    const response = await fetch('/api.php?action=reviews', {
      credentials: 'include',
      cache: 'no-store',
    });
    if (!response.ok) return null;
    return await response.json() as ReviewsData;
  } catch {
    return null;
  }
};

const mergeSiteData = (defaults: SiteData, siteOverrides: Partial<SiteData> | null): SiteData => {
  if (!siteOverrides || Object.keys(siteOverrides).length === 0) {
    return defaults;
  }

  const result: SiteData = { ...defaults };

  if (siteOverrides.settings) {
    result.settings = deepMerge(
      defaults.settings as unknown as Record<string, unknown>,
      siteOverrides.settings as unknown as Record<string, unknown>
    ) as unknown as SiteSettings;
  }

  if (siteOverrides.homepage) {
    result.homepage = deepMerge(
      defaults.homepage as unknown as Record<string, unknown>,
      siteOverrides.homepage as unknown as Record<string, unknown>
    ) as unknown as HomepageSettings;
  }

  if (siteOverrides.alpacas) result.alpacas = siteOverrides.alpacas;
  if (siteOverrides.activities) result.activities = siteOverrides.activities;
  if (siteOverrides.faqs) result.faqs = siteOverrides.faqs;
  if (siteOverrides.reviews) result.reviews = siteOverrides.reviews;

  return result;
};

const loadConsolidatedData = async (): Promise<void> => {
  if (cachedDefaultData) return;
  if (dataLoadPromise) return dataLoadPromise;

  dataLoadPromise = (async () => {
    if (isProduction()) {
      const injectedSiteData = getInjectedSiteData();

      if (injectedSiteData) {
        const merged = cloneSiteData(injectedSiteData);
        const reviewsFromApi = await fetchProductionReviews();

        if (reviewsFromApi) {
          merged.reviews = reviewsFromApi;
        }

        cachedBaseDefaultData = cloneSiteData(merged);
        cachedDefaultData = cloneSiteData(merged);
        cachedSiteData = null;
        markSiteDataReady('inline');
        return;
      }

      const [siteDataFromApi, reviewsFromApi] = await Promise.all([
        fetchProductionSiteData(),
        fetchProductionReviews(),
      ]);

      const merged = cloneSiteData(siteDataFromApi);
      cachedBaseDefaultData = cloneSiteData(siteDataFromApi);

      if (reviewsFromApi) {
        merged.reviews = reviewsFromApi;
      }

      cachedSiteData = null;
      cachedDefaultData = merged;
      markSiteDataReady('api');
      return;
    }

    // Development: use bundled defaults + localStorage overrides
    cachedBaseDefaultData = getBundledDefaultData();
    cachedDefaultData = cachedBaseDefaultData;
    cachedSiteData = storage.get<Partial<SiteData> | null>(STORAGE_KEYS.siteData, null);
    markSiteDataReady('dev');
  })();

  return dataLoadPromise;
};

export const preloadSiteData = async (): Promise<void> => {
  if (!isProduction()) return;
  if (cachedDefaultData) return;
  if (productionBootstrapPromise) return productionBootstrapPromise;

  productionBootstrapPromise = loadConsolidatedData().finally(() => {
    productionBootstrapPromise = null;
  });

  return productionBootstrapPromise;
};

const getMergedData = async (): Promise<SiteData> => {
  await loadConsolidatedData();

  if (isProduction()) {
    return cachedDefaultData!;
  }

  return mergeSiteData(cachedDefaultData!, cachedSiteData);
};

// Invalidate cache (after saves)
const invalidateCache = () => {
  cachedSiteData = null;
  dataLoadPromise = null;
  cachedDefaultData = null;
  cachedBaseDefaultData = null;
  productionBootstrapPromise = null;
};

// ===== LocalStorage (development mode) =====

const STORAGE_KEYS = {
  siteData: 'alpaca_site_data',
  uploadedImages: 'alpaca_data_uploaded_images',
  adminAuth: 'alpaca_admin_auth',
  sessionToken: 'alpaca_session_token'
};

const storage = {
  get: <T>(key: string, defaultValue: T): T => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  },
  set: <T>(key: string, value: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('LocalStorage write error:', error);
    }
  },
  remove: (key: string): void => {
    localStorage.removeItem(key);
  }
};

// Get dev site data (from localStorage, merged with defaults)
const getDevSiteData = async (): Promise<SiteData> => {
  await loadConsolidatedData();
  const stored = storage.get<Partial<SiteData> | null>(STORAGE_KEYS.siteData, null);
  return mergeSiteData(cachedBaseDefaultData || getBundledDefaultData(), stored);
};

const saveDevSiteData = async (data: Partial<SiteData>) => {
  const current = await getDevSiteData();
  const merged = { ...current, ...data };
  storage.set(STORAGE_KEYS.siteData, merged);
  cachedSiteData = merged;
};

// ===== Production API calls =====

const productionApi = {
  fetch: async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
    const response = await fetch(`/api.php${endpoint}`, {
      ...options,
      credentials: 'include',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', ...options.headers },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || 'Request failed');
    }

    return response.json();
  },

  fetchWithCsrf: async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
    const token = await getCsrfToken();
    const result = await productionApi.fetch<T>(endpoint, {
      ...options,
      headers: { ...options.headers, 'X-CSRF-Token': token },
    });

    const method = (options.method || 'GET').toUpperCase();
    const shouldRegenerateStaticPages =
      isProduction() && endpoint.includes('action=save-section') && method !== 'GET';

    if (shouldRegenerateStaticPages) {
      await regenerateStaticPages();
    }

    return result;
  },

  uploadFile: async (file: File): Promise<UploadedImage> => {
    const token = await getCsrfToken();
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch('/api.php?action=upload', {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      headers: { 'X-CSRF-Token': token },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || 'Upload failed');
    }

    return response.json();
  },
};

interface StaticPagePayload {
  route: string;
  html: string;
}

const waitForStaticPageReady = (iframe: HTMLIFrameElement, route: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const timeoutMs = 20000;

    const checkReady = () => {
      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;

      if (!doc || !win) {
        if (Date.now() - startedAt > timeoutMs) {
          reject(new Error(`Kon ${route} niet openen voor HTML-verversing.`));
          return;
        }

        window.requestAnimationFrame(checkReady);
        return;
      }

      const isDataReady = doc.documentElement.getAttribute(SITE_DATA_READY_ATTRIBUTE) === 'true';
      const isAppRendered = doc.documentElement.getAttribute(APP_RENDERED_ATTRIBUTE) === 'true';

      if (isDataReady && isAppRendered) {
        resolve();
        return;
      }

      if (Date.now() - startedAt > timeoutMs) {
        reject(new Error(`Timeout tijdens HTML-verversing van ${route}.`));
        return;
      }

      win.requestAnimationFrame(checkReady);
    };

    iframe.addEventListener('load', checkReady, { once: true });
  });

const captureStaticPage = async (route: string, siteData: SiteData): Promise<StaticPagePayload> => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.left = '-99999px';
  iframe.style.top = '0';
  iframe.style.width = '1440px';
  iframe.style.height = '2200px';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  iframe.style.border = '0';

  const refreshUrl = new URL(route, window.location.origin);
  refreshUrl.searchParams.set(INLINE_SNAPSHOT_BYPASS_PARAM, Date.now().toString());

  document.body.appendChild(iframe);

  try {
    const ready = waitForStaticPageReady(iframe, route);
    iframe.src = refreshUrl.toString();
    await ready;

    const doc = iframe.contentDocument;
    if (!doc) {
      throw new Error(`Geen document gevonden voor ${route}.`);
    }

    sanitizeStaticDocument(doc, siteData);

    return {
      route,
      html: `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`,
    };
  } finally {
    iframe.remove();
  }
};

const captureStaticPages = async (siteData: SiteData): Promise<StaticPagePayload[]> => {
  const pages: StaticPagePayload[] = [];

  for (const route of STATIC_PAGE_ROUTES) {
    pages.push(await captureStaticPage(route, siteData));
  }

  return pages;
};

const regenerateStaticPages = async (): Promise<void> => {
  if (!isProduction() || typeof window === 'undefined') return;
  if (staticPageRegenerationPromise) return staticPageRegenerationPromise;

  staticPageRegenerationPromise = (async () => {
    const [siteDataFromApi, reviewsFromApi] = await Promise.all([
      fetchProductionSiteData(),
      fetchProductionReviews(),
    ]);

    const merged = cloneSiteData(siteDataFromApi);
    if (reviewsFromApi) {
      merged.reviews = reviewsFromApi;
    }

    const pages = await captureStaticPages(merged);

    await productionApi.fetchWithCsrf<{ success: boolean }>('?action=write-static-pages', {
      method: 'POST',
      body: JSON.stringify({ pages }),
    });

    cachedBaseDefaultData = cloneSiteData(merged);
    cachedDefaultData = cloneSiteData(merged);
    cachedSiteData = null;
    markSiteDataReady('inline');
  })().finally(() => {
    staticPageRegenerationPromise = null;
  });

  return staticPageRegenerationPromise;
};

// ===== Normalize activities =====

const normalizeActivities = (activities: Activity[], fallbackActivities: Activity[]): Activity[] => {
  return activities.map((activity, index) => {
    const activityDefaults = fallbackActivities.find((d) => d.id === activity.id) || fallbackActivities[index];
    if (!activityDefaults) return activity;
    return { ...activityDefaults, ...activity };
  });
};

// ===== Synchronous data access (for public pages — no flicker) =====

const getSyncMergedData = (): SiteData => {
  // Production: cachedDefaultData is populated by preloadSiteData() before React renders
  if (isProduction()) {
    if (!cachedDefaultData) {
      // Should never happen — preloadSiteData() runs before React mount
      return getBundledDefaultData();
    }
    return cachedDefaultData;
  }

  // Development: use bundled defaults + localStorage overrides (all synchronous)
  const base = getBundledDefaultData();
  const stored = storage.get<Partial<SiteData> | null>(STORAGE_KEYS.siteData, null);
  return mergeSiteData(base, stored);
};

export const apiSync = {
  getSiteSettings: (): SiteSettings => migrateSiteSettings(getSyncMergedData().settings),
  getHomepageSettings: (): HomepageSettings => getSyncMergedData().homepage,
  getAlpacas: (): Alpaca[] => getSyncMergedData().alpacas,
  getActivities: (): Activity[] => {
    const data = getSyncMergedData();
    return normalizeActivities(data.activities, bundledDefaultData.activities);
  },
  getFAQs: (): FAQ[] => getSyncMergedData().faqs,
  getReviews: (): ReviewsData => getSyncMergedData().reviews,
};

// ===== Main API =====

export const api = {
  // Alpacas
  getAlpacas: async (): Promise<Alpaca[]> => {
    if (isProduction()) {
      const data = await getMergedData();
      return data.alpacas;
    }
    return (await getDevSiteData()).alpacas;
  },

  getAlpaca: async (id: number): Promise<Alpaca | undefined> => {
    const alpacas = await api.getAlpacas();
    return alpacas.find(a => a.id === id);
  },

  createAlpaca: async (alpaca: Omit<Alpaca, 'id'>): Promise<Alpaca> => {
    if (isProduction()) {
      const result = await productionApi.fetchWithCsrf<Alpaca>('?action=save-section&section=alpacas&op=create', {
        method: 'POST',
        body: JSON.stringify(alpaca),
      });
      invalidateCache();
      return result;
    }

    const data = await getDevSiteData();
    const maxId = data.alpacas.reduce((max, a) => Math.max(max, a.id), 0);
    const newAlpaca = { ...alpaca, id: maxId + 1 } as Alpaca;
    data.alpacas.push(newAlpaca);
    await saveDevSiteData({ alpacas: data.alpacas });
    return newAlpaca;
  },

  updateAlpaca: async (id: number, updates: Partial<Alpaca>): Promise<Alpaca> => {
    if (isProduction()) {
      const result = await productionApi.fetchWithCsrf<Alpaca>(`?action=save-section&section=alpacas&op=update&id=${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      invalidateCache();
      return result;
    }

    const data = await getDevSiteData();
    const index = data.alpacas.findIndex(a => a.id === id);
    if (index === -1) throw new Error('Alpaca not found');
    data.alpacas[index] = { ...data.alpacas[index], ...updates };
    await saveDevSiteData({ alpacas: data.alpacas });
    return data.alpacas[index];
  },

  deleteAlpaca: async (id: number): Promise<void> => {
    if (isProduction()) {
      await productionApi.fetchWithCsrf<void>(`?action=save-section&section=alpacas&op=delete&id=${id}`, {
        method: 'DELETE',
      });
      invalidateCache();
      return;
    }

    const data = await getDevSiteData();
    await saveDevSiteData({ alpacas: data.alpacas.filter(a => a.id !== id) });
  },

  // Activities
  getActivities: async (): Promise<Activity[]> => {
    if (isProduction()) {
      const data = await getMergedData();
      return normalizeActivities(data.activities, cachedBaseDefaultData?.activities || []);
    }
    const data = await getDevSiteData();
    return normalizeActivities(data.activities, cachedBaseDefaultData?.activities || []);
  },

  getActivity: async (id: number): Promise<Activity | undefined> => {
    const activities = await api.getActivities();
    return activities.find(a => a.id === id);
  },

  createActivity: async (activity: Omit<Activity, 'id'>): Promise<Activity> => {
    if (isProduction()) {
      const result = await productionApi.fetchWithCsrf<Activity>('?action=save-section&section=activities&op=create', {
        method: 'POST',
        body: JSON.stringify(activity),
      });
      invalidateCache();
      return result;
    }

    const data = await getDevSiteData();
    const maxId = data.activities.reduce((max, a) => Math.max(max, a.id), 0);
    const newActivity = { ...activity, id: maxId + 1 } as Activity;
    data.activities.push(newActivity);
    await saveDevSiteData({ activities: data.activities });
    return newActivity;
  },

  updateActivity: async (id: number, updates: Partial<Activity>): Promise<Activity> => {
    if (isProduction()) {
      const result = await productionApi.fetchWithCsrf<Activity>(`?action=save-section&section=activities&op=update&id=${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      invalidateCache();
      return result;
    }

    const data = await getDevSiteData();
    const index = data.activities.findIndex(a => a.id === id);
    if (index === -1) throw new Error('Activity not found');
    data.activities[index] = { ...data.activities[index], ...updates };
    await saveDevSiteData({ activities: data.activities });
    return data.activities[index];
  },

  reorderActivities: async (orderedActivities: Activity[]): Promise<Activity[]> => {
    if (isProduction()) {
      const result = await productionApi.fetchWithCsrf<Activity[]>('?action=save-section&section=activities', {
        method: 'PUT',
        body: JSON.stringify(orderedActivities),
      });
      invalidateCache();
      return result;
    }

    await saveDevSiteData({ activities: orderedActivities });
    return orderedActivities;
  },

  // FAQs
  getFAQs: async (): Promise<FAQ[]> => {
    if (isProduction()) {
      const data = await getMergedData();
      return data.faqs;
    }
    return (await getDevSiteData()).faqs;
  },

  createFAQ: async (faq: Omit<FAQ, 'id'>): Promise<FAQ> => {
    if (isProduction()) {
      const result = await productionApi.fetchWithCsrf<FAQ>('?action=save-section&section=faqs&op=create', {
        method: 'POST',
        body: JSON.stringify(faq),
      });
      invalidateCache();
      return result;
    }

    const data = await getDevSiteData();
    const maxId = data.faqs.reduce((max, f) => Math.max(max, f.id), 0);
    const newFAQ = { ...faq, id: maxId + 1 } as FAQ;
    data.faqs.push(newFAQ);
    await saveDevSiteData({ faqs: data.faqs });
    return newFAQ;
  },

  updateFAQ: async (id: number, updates: Partial<FAQ>): Promise<FAQ> => {
    if (isProduction()) {
      const result = await productionApi.fetchWithCsrf<FAQ>(`?action=save-section&section=faqs&op=update&id=${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      invalidateCache();
      return result;
    }

    const data = await getDevSiteData();
    const index = data.faqs.findIndex(f => f.id === id);
    if (index === -1) throw new Error('FAQ not found');
    data.faqs[index] = { ...data.faqs[index], ...updates };
    await saveDevSiteData({ faqs: data.faqs });
    return data.faqs[index];
  },

  deleteFAQ: async (id: number): Promise<void> => {
    if (isProduction()) {
      await productionApi.fetchWithCsrf<void>(`?action=save-section&section=faqs&op=delete&id=${id}`, {
        method: 'DELETE',
      });
      invalidateCache();
      return;
    }

    const data = await getDevSiteData();
    await saveDevSiteData({ faqs: data.faqs.filter(f => f.id !== id) });
  },

  // Homepage Settings
  getHomepageSettings: async (): Promise<HomepageSettings> => {
    if (isProduction()) {
      const data = await getMergedData();
      return data.homepage;
    }
    return (await getDevSiteData()).homepage;
  },

  updateHomepageSettings: async (settings: HomepageSettings): Promise<HomepageSettings> => {
    if (isProduction()) {
      const result = await productionApi.fetchWithCsrf<HomepageSettings>('?action=save-section&section=homepage', {
        method: 'PUT',
        body: JSON.stringify(settings),
      });
      invalidateCache();
      return result;
    }

    await saveDevSiteData({ homepage: settings });
    return settings;
  },

  // Site Settings
  getSiteSettings: async (): Promise<SiteSettings> => {
    if (isProduction()) {
      const data = await getMergedData();
      return migrateSiteSettings(data.settings);
    }
    return migrateSiteSettings((await getDevSiteData()).settings);
  },

  updateSiteSettings: async (settings: SiteSettings): Promise<SiteSettings> => {
    if (isProduction()) {
      const result = await productionApi.fetchWithCsrf<SiteSettings>('?action=save-section&section=settings', {
        method: 'PUT',
        body: JSON.stringify(settings),
      });
      invalidateCache();
      return result;
    }

    await saveDevSiteData({ settings });
    return settings;
  },

  // Reviews
  getReviews: async (): Promise<ReviewsData> => {
    if (isProduction()) {
      const data = await getMergedData();
      return data.reviews;
    }
    return (await getDevSiteData()).reviews;
  },

  // Image uploads
  getUploadedImages: async (): Promise<UploadedImage[]> => {
    if (isProduction()) {
      return await productionApi.fetch<UploadedImage[]>('?action=images');
    }
    return storage.get(STORAGE_KEYS.uploadedImages, []);
  },

  uploadImage: async (file: File): Promise<UploadedImage> => {
    if (isProduction()) {
      return await productionApi.uploadFile(file);
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const newImage: UploadedImage = {
          id: `img_${Date.now()}`,
          name: file.name,
          url: reader.result as string,
          uploadedAt: new Date().toISOString()
        };
        const images = storage.get<UploadedImage[]>(STORAGE_KEYS.uploadedImages, []);
        images.unshift(newImage);
        storage.set(STORAGE_KEYS.uploadedImages, images);
        resolve(newImage);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  },

  deleteImage: async (id: string): Promise<void> => {
    if (isProduction()) {
      await productionApi.fetchWithCsrf<void>(`?action=delete-image&id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
      return;
    }

    const images = storage.get<UploadedImage[]>(STORAGE_KEYS.uploadedImages, []);
    storage.set(STORAGE_KEYS.uploadedImages, images.filter(img => img.id !== id));
  },

  renameImage: async (id: string, newName: string): Promise<UploadedImage> => {
    if (isProduction()) {
      return await productionApi.fetchWithCsrf<UploadedImage>(`?action=rename-image&id=${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify({ name: newName }),
      });
    }

    const images = storage.get<UploadedImage[]>(STORAGE_KEYS.uploadedImages, []);
    const index = images.findIndex(img => img.id === id);
    if (index === -1) throw new Error('Image not found');
    images[index] = { ...images[index], name: newName };
    storage.set(STORAGE_KEYS.uploadedImages, images);
    return images[index];
  },

  // Reset to defaults (development only)
  resetToDefaults: async (): Promise<void> => {
    storage.remove(STORAGE_KEYS.siteData);
  },
};

// ===== Authentication =====

export const auth = {
  login: async (username: string, password: string): Promise<boolean> => {
    if (isProduction()) {
      try {
        const response = await productionApi.fetch<{ success: boolean }>('?action=login', {
          method: 'POST',
          body: JSON.stringify({ username, password }),
        });

        if (response.success) {
          storage.set(STORAGE_KEYS.sessionToken, 'authenticated');
          csrfToken = null;
        }

        return response.success;
      } catch {
        return false;
      }
    }

    if (import.meta.env.DEV) {
      storage.set(STORAGE_KEYS.adminAuth, true);
      return true;
    }

    return false;
  },

  logout: async (): Promise<void> => {
    if (isProduction()) {
      try {
        await productionApi.fetch('?action=logout', { method: 'POST' });
      } catch {
        /* ignore */
      }
    }

    storage.remove(STORAGE_KEYS.adminAuth);
    storage.remove(STORAGE_KEYS.sessionToken);
    csrfToken = null;
  },

  isAuthenticated: (): boolean => {
    if (isProduction()) {
      return !!localStorage.getItem(STORAGE_KEYS.sessionToken);
    }
    return storage.get(STORAGE_KEYS.adminAuth, false);
  },

  validateSession: async (): Promise<boolean> => {
    if (isProduction()) {
      try {
        const response = await productionApi.fetch<{ valid: boolean }>('?action=validate');
        if (!response.valid) {
          storage.remove(STORAGE_KEYS.sessionToken);
        }
        return response.valid;
      } catch {
        storage.remove(STORAGE_KEYS.sessionToken);
        return false;
      }
    }

    return auth.isAuthenticated();
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<boolean> => {
    if (isProduction()) {
      try {
        const response = await productionApi.fetchWithCsrf<{ success: boolean }>('?action=change-password', {
          method: 'POST',
          body: JSON.stringify({ currentPassword, newPassword }),
        });
        return response.success;
      } catch {
        return false;
      }
    }

    return false;
  },
};
